import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Inbox, Send, FlaskConical, Trash2, Users, ChevronDown, ChevronUp, ExternalLink, Save, RotateCcw, History, Mail, Archive, ArchiveRestore } from "lucide-react";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");

/**
 * Iter122bb · Onboarding studi — sezione unificata SuperAdmin.
 * Merge di Richieste di prova (in arrivo dal form pubblico /richiedi-prova)
 * e Proposta agli studi (outreach agli amministratori dell'anagrafica).
 *
 * FIX iter122bb (bug utente): la categoria della proposta era text libero e
 * l'utente non poteva caricare i contatti categoria "Studi Amministrazione".
 * Ora è una SELECT popolata da /api/categorie con default preselezionato.
 *
 * Props:
 * - onCreaProvaDaRichiesta(richiesta): callback per pre-compilare Trial da una richiesta.
 */
export default function SuperAdminOnboarding({ onCreaProvaDaRichiesta }) {
  const { user } = useAuth();
  const hasTenantScope = user?.role === "admin" && user?.tenant_id === "studio";
  const [richieste, setRichieste] = useState([]);
  const [showRichieste, setShowRichieste] = useState(true);
  const [showArchiviate, setShowArchiviate] = useState(false);
  const [showProposta, setShowProposta] = useState(true);
  const [categorie, setCategorie] = useState([]);
  const [propRecips, setPropRecips] = useState([]);
  const [propSel, setPropSel] = useState({});
  const [propCat, setPropCat] = useState("Studi Amministrazione");
  const [propSearch, setPropSearch] = useState("");
  const [propText, setPropText] = useState({ oggetto: "", testo: "" });
  const [propBusy, setPropBusy] = useState(false);
  const [propLoaded, setPropLoaded] = useState(false);
  // Iter122bo · template proposta salvato persistente
  const [propTextInitial, setPropTextInitial] = useState({ oggetto: "", testo: "" });
  const [propSaving, setPropSaving] = useState(false);
  // Iter122bp · storico invii
  const [storico, setStorico] = useState([]);
  const [storicoAggr, setStoricoAggr] = useState(null);
  const [storicoOpen, setStoricoOpen] = useState(false);
  const [storicoDetail, setStoricoDetail] = useState(null);

  const loadStorico = () => api.get("/admin/proposta-studi/storico?limit=200").then((r) => {
    setStorico(r.data.items || []);
    setStoricoAggr(r.data.aggregato || null);
  }).catch(() => {});

  const loadRichieste = () => api.get("/admin/richieste-prova").then((r) => setRichieste(r.data.richieste || [])).catch(() => {});
  const loadCategorie = () => api.get("/categorie").then((r) => setCategorie(r.data.categorie || [])).catch(() => {});
  const loadTemplate = () => api.get("/admin/proposta-studi/template").then((r) => {
    const t = { oggetto: r.data.oggetto || "", testo: r.data.testo || "" };
    setPropText(t); setPropTextInitial(t);
  }).catch(() => {});

  useEffect(() => {
    loadRichieste();
    loadCategorie();
    loadTemplate();  // Iter122bo · carica il testo salvato invece dell'hardcoded
    loadStorico();   // Iter122bp · carica storico invii proposta
  }, []);

  // Iter122bb · richiama loadRichieste dall'esterno quando viene creato un trial
  // (esposto tramite ref? no, semplice: onCreaProvaDaRichiesta chiama loadRichieste in modo autonomo)

  const setRichiestaStato = async (r, stato) => {
    // Iter122ez · UX: se la richiesta è già "attivata" (trial creato) chiediamo
    // conferma esplicita prima di archiviarla — il Trial resta attivo, la
    // richiesta viene solo spostata nella sezione "Archiviate".
    if (stato === "archiviata" && r.stato === "attivata") {
      if (!window.confirm(
        `Archiviare la richiesta di "${r.studio_nome}"?\n\n` +
        `Il Trial già attivato rimarrà attivo — la richiesta verrà spostata nella sezione "Richieste archiviate".\n` +
        `Per disattivare il Trial usa la scheda "Trial attivi".`
      )) return;
    }
    try {
      await api.post(`/admin/richieste-prova/${r.id}/stato`, { stato });
      loadRichieste();
      const label = stato === "archiviata" ? "archiviata" : stato === "contattata" ? "segnata contattata" : stato === "nuova" ? "ripristinata" : `impostata su ${stato}`;
      toast.success(`Richiesta ${label}`);
    }
    catch { toast.error("Operazione non riuscita"); }
  };
  // Iter122ez · Ripristina una richiesta archiviata: torna al suo stato "logico"
  // (attivata se ha già account_id, altrimenti nuova).
  const ripristinaRichiesta = async (r) => {
    const nuovoStato = r.account_id ? "attivata" : "nuova";
    setRichiestaStato(r, nuovoStato);
  };
  const delRichiesta = async (r) => {
    if (!window.confirm(`Eliminare la richiesta di "${r.studio_nome}"?`)) return;
    try {
      await api.delete(`/admin/richieste-prova/${r.id}`);
      loadRichieste();
      toast.success("Richiesta eliminata");
    } catch { toast.error("Errore"); }
  };

  const loadPropRecips = async () => {
    setPropBusy(true);
    try {
      const { data } = await api.get("/fornitori", { params: { limit: 5000, ...(propCat ? { categoria: propCat } : {}), ...(propSearch ? { search: propSearch } : {}) } });
      setPropRecips(data.fornitori || []); setPropLoaded(true);
      if ((data.fornitori || []).length === 0) toast.info("Nessun contatto in questa categoria: prova una categoria diversa o crea prima i fornitori in Anagrafica.");
    } catch (e) { toast.error("Caricamento contatti non riuscito"); }
    setPropBusy(false);
  };
  const toggleSel = (id) => setPropSel((s) => ({ ...s, [id]: !s[id] }));
  const selVisibili = (val) => setPropSel(() => { const o = {}; propRecips.forEach((f) => { if (f.email) o[f.id] = val; }); return o; });
  const inviaProposta = async () => {
    const ids = Object.keys(propSel).filter((k) => propSel[k]);
    if (ids.length === 0) { toast.error("Seleziona almeno un destinatario (con email)"); return; }
    if (!window.confirm(`Inviare la proposta a ${ids.length} contatti selezionati?`)) return;
    setPropBusy(true);
    try {
      const { data } = await api.post("/admin/proposta-studi/invia", { fornitore_ids: ids, oggetto: propText.oggetto, testo: propText.testo });
      toast.success(`Invio avviato: ${data.inviate} email in coda (${data.con_email} con email, ${data.senza_email} senza). In anteprima il limite test è 1 destinatario.`);
      setPropSel({});
      loadStorico();  // Iter122bp · refresh storico dopo invio
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio non riuscito"); }
    setPropBusy(false);
  };
  const selCount = Object.values(propSel).filter(Boolean).length;

  // Iter122bo · Save/reset template
  const salvaTemplate = async () => {
    if (!propText.oggetto.trim() || !propText.testo.trim()) { toast.error("Oggetto e messaggio obbligatori"); return; }
    setPropSaving(true);
    try {
      await api.post("/admin/proposta-studi/template", { oggetto: propText.oggetto, testo: propText.testo });
      setPropTextInitial({ ...propText });
      toast.success("Modello proposta salvato · verrà usato nei prossimi invii");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore salvataggio"); }
    setPropSaving(false);
  };
  const ripristinaTemplate = () => setPropText({ ...propTextInitial });
  const propTextDirty = propText.oggetto !== propTextInitial.oggetto || propText.testo !== propTextInitial.testo;

  // Iter122ez · Split richieste attive vs archiviate — le archiviate hanno una
  // sezione dedicata collassabile, non intasano più la lista principale.
  const richiesteAttive = richieste.filter((r) => r.stato !== "archiviata");
  const richiesteArchiviate = richieste.filter((r) => r.stato === "archiviata");

  return (
    <div className="space-y-8" data-testid="superadmin-onboarding">
      {/* RICHIESTE DI PROVA */}
      <section>
        <button
          onClick={() => setShowRichieste((v) => !v)}
          data-testid="onboarding-toggle-richieste"
          className="w-full flex items-center justify-between p-4 rounded-t-lg bg-slate-50 border-2 border-b-0 border-slate-200 hover:bg-slate-100 transition-colors">
          <div className="flex items-center gap-2">
            <Inbox className="w-5 h-5 text-brand" />
            <h3 className="font-display text-lg font-bold text-slate-800">Richieste di prova in arrivo</h3>
            {richiesteAttive.length > 0 && <span className="ml-2 px-2 py-0.5 rounded-full bg-brand/10 text-brand text-xs font-bold">{richiesteAttive.length}</span>}
          </div>
          {showRichieste ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
        </button>
        {showRichieste && (
          <Card className="p-5 border-2 border-t-0 rounded-t-none" data-testid="richieste-prova-card">
            <p className="text-sm text-slate-500 mb-4">Richieste arrivate dal modulo pubblico <code>/richiedi-prova</code>. Clic su "Crea prova" precompila il form Trial qui sopra.</p>
            {richiesteAttive.length === 0 ? (
              <div className="text-center text-slate-400 py-4">Nessuna richiesta ricevuta.</div>
            ) : (
              <div className="space-y-3" data-testid="richieste-prova-list">
                {richiesteAttive.map((r) => (
                  <div key={r.id} className="border-2 border-slate-100 rounded-lg p-3" data-testid={`richiesta-${r.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-bold text-slate-800">{r.studio_nome}
                          <span className={`ml-2 text-[11px] font-semibold px-2 py-0.5 rounded-full ${r.stato === "attivata" ? "bg-emerald-100 text-emerald-700" : r.stato === "archiviata" ? "bg-slate-100 text-slate-500" : r.stato === "contattata" ? "bg-amber-100 text-amber-700" : "bg-brand/10 text-brand"}`}>{r.stato}</span>
                        </div>
                        <div className="text-sm text-slate-600">{r.referente} · {r.email} {r.telefono ? `· ${r.telefono}` : ""}</div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {r.citta && <>{r.citta} · </>}{r.num_condomini && <>{r.num_condomini} condomìni · </>}{r.partita_iva && <>P.IVA {r.partita_iva} · </>}{fmtDate(r.created_at)}
                        </div>
                        {r.note && <div className="text-xs text-slate-500 mt-1 italic">"{r.note}"</div>}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {r.stato !== "attivata" && (
                          <Button size="sm" onClick={() => onCreaProvaDaRichiesta && onCreaProvaDaRichiesta(r)} data-testid={`richiesta-crea-${r.id}`} className="bg-brand hover:bg-brand-dark h-9"><FlaskConical className="w-4 h-4 mr-1" /> Crea prova</Button>
                        )}
                        {r.stato === "nuova" && <Button size="sm" variant="outline" onClick={() => setRichiestaStato(r, "contattata")} data-testid={`richiesta-contattata-${r.id}`} className="h-9">Segna contattata</Button>}
                        <Button size="sm" variant="outline" onClick={() => setRichiestaStato(r, "archiviata")} data-testid={`richiesta-archivia-${r.id}`} className="h-9">Archivia</Button>
                        <Button size="sm" variant="outline" onClick={() => delRichiesta(r)} data-testid={`richiesta-del-${r.id}`} className="h-9 text-red-600"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </section>

      {/* Iter122ez · RICHIESTE ARCHIVIATE (sezione separata collassabile) */}
      <section>
        <button
          onClick={() => setShowArchiviate((v) => !v)}
          data-testid="onboarding-toggle-archiviate"
          className="w-full flex items-center justify-between p-4 rounded-t-lg bg-slate-50 border-2 border-b-0 border-slate-200 hover:bg-slate-100 transition-colors">
          <div className="flex items-center gap-2">
            <Archive className="w-5 h-5 text-slate-500" />
            <h3 className="font-display text-lg font-bold text-slate-800">Richieste archiviate</h3>
            {richiesteArchiviate.length > 0 && <span className="ml-2 px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-xs font-bold">{richiesteArchiviate.length}</span>}
          </div>
          {showArchiviate ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
        </button>
        {showArchiviate && (
          <Card className="p-5 border-2 border-t-0 rounded-t-none bg-slate-50/40" data-testid="richieste-archiviate-card">
            <p className="text-sm text-slate-500 mb-4">Richieste rimosse dalla lista attiva. I Trial già creati rimangono attivi; per disattivarli usa la scheda <b>Trial attivi</b>.</p>
            {richiesteArchiviate.length === 0 ? (
              <div className="text-center text-slate-400 py-4">Nessuna richiesta archiviata.</div>
            ) : (
              <div className="space-y-3" data-testid="richieste-archiviate-list">
                {richiesteArchiviate.map((r) => (
                  <div key={r.id} className="border-2 border-slate-100 bg-white rounded-lg p-3 opacity-90" data-testid={`richiesta-archiviata-${r.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-bold text-slate-700">{r.studio_nome}
                          <span className="ml-2 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">archiviata</span>
                          {r.account_id && <span className="ml-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Trial attivo</span>}
                        </div>
                        <div className="text-sm text-slate-500">{r.referente} · {r.email} {r.telefono ? `· ${r.telefono}` : ""}</div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {r.citta && <>{r.citta} · </>}{r.num_condomini && <>{r.num_condomini} condomìni · </>}{r.partita_iva && <>P.IVA {r.partita_iva} · </>}{fmtDate(r.created_at)}
                        </div>
                        {r.note && <div className="text-xs text-slate-500 mt-1 italic">"{r.note}"</div>}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => ripristinaRichiesta(r)} data-testid={`richiesta-ripristina-${r.id}`} className="h-9 border-brand text-brand hover:bg-brand hover:text-white">
                          <ArchiveRestore className="w-4 h-4 mr-1" /> Ripristina
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => delRichiesta(r)} data-testid={`richiesta-del-arch-${r.id}`} className="h-9 text-red-600"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </section>

      {/* PROPOSTA / OUTREACH */}
      <section>
        <button
          onClick={() => setShowProposta((v) => !v)}
          data-testid="onboarding-toggle-proposta"
          className="w-full flex items-center justify-between p-4 rounded-t-lg bg-slate-50 border-2 border-b-0 border-slate-200 hover:bg-slate-100 transition-colors">
          <div className="flex items-center gap-2">
            <Send className="w-5 h-5 text-brand" />
            <h3 className="font-display text-lg font-bold text-slate-800">Proposta di prova agli studi</h3>
          </div>
          {showProposta ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
        </button>
        {showProposta && (
          <Card className="p-4 sm:p-5 border-2 border-t-0 rounded-t-none min-w-0 overflow-x-hidden" data-testid="proposta-studi-card">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <p className="text-sm text-slate-500 flex-1 min-w-[200px]">Invia la proposta a una lista di contatti dell'<b>anagrafica fornitori</b>. Filtra per categoria (di default gli <b>Studi Amministrazione</b>), cerca e flagga i destinatari.</p>
              {/* Iter122bc · collegamento diretto all'Anagrafica fornitori filtrata per gestire gli studi */}
              {hasTenantScope && (
                <a href={`/fornitori?categoria=${encodeURIComponent(propCat || "Studi Amministrazione")}`}
                   target="_blank" rel="noopener noreferrer"
                   data-testid="proposta-open-anagrafica"
                   className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 border-brand text-brand hover:bg-brand/5 text-sm font-bold whitespace-nowrap transition-colors">
                  <ExternalLink className="w-4 h-4" />
                  Gestisci in Anagrafica
                </a>
              )}
            </div>
            <div className="grid lg:grid-cols-2 gap-4 min-w-0">
              <div className="min-w-0">
                <label className="text-xs font-semibold text-slate-500">Oggetto</label>
                <Input className="h-11 mt-1 mb-3 w-full" data-testid="proposta-oggetto" value={propText.oggetto} onChange={(e) => setPropText({ ...propText, oggetto: e.target.value })} />
                <label className="text-xs font-semibold text-slate-500">Messaggio</label>
                <Textarea data-testid="proposta-testo" rows={11} className="mt-1 text-sm leading-relaxed w-full" value={propText.testo} onChange={(e) => setPropText({ ...propText, testo: e.target.value })} />
                {/* Iter122bo · Salva/ripristina modello */}
                <div className="flex flex-wrap gap-2 mt-2 items-center">
                  <Button size="sm" variant="outline" onClick={salvaTemplate} disabled={propSaving || !propTextDirty}
                          data-testid="proposta-salva-modello" className="h-9 border-brand text-brand hover:bg-brand hover:text-white">
                    <Save className="w-4 h-4 mr-1" /> {propSaving ? "Salvataggio…" : "Salva modello"}
                  </Button>
                  {propTextDirty && (
                    <Button size="sm" variant="outline" onClick={ripristinaTemplate} data-testid="proposta-ripristina" className="h-9 text-slate-500">
                      <RotateCcw className="w-4 h-4 mr-1" /> Ripristina
                    </Button>
                  )}
                  {propTextDirty && <span className="text-xs text-amber-700 font-semibold">Modifiche non salvate</span>}
                  {!propTextDirty && propTextInitial.oggetto && <span className="text-xs text-emerald-700 font-semibold">✓ Modello salvato</span>}
                </div>
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap gap-2 mb-2">
                  <div className="flex-1 min-w-0 basis-full sm:basis-[45%]">
                    <label className="text-xs font-semibold text-slate-500">Categoria (dall'anagrafica)</label>
                    <select
                      data-testid="proposta-categoria"
                      value={propCat}
                      onChange={(e) => setPropCat(e.target.value)}
                      className="w-full h-10 mt-1 rounded-md border-2 border-slate-200 bg-white px-3 text-sm font-semibold focus:border-brand outline-none">
                      <option value="">— Tutte le categorie —</option>
                      {categorie.map((c) => (<option key={c} value={c}>{c}</option>))}
                    </select>
                  </div>
                  <div className="flex-1 min-w-0 basis-[60%] sm:basis-[35%]">
                    <label className="text-xs font-semibold text-slate-500">Cerca (nome/città)</label>
                    <Input className="h-10 mt-1 w-full" placeholder="es. Milano" data-testid="proposta-search" value={propSearch} onChange={(e) => setPropSearch(e.target.value)} />
                  </div>
                  <Button variant="outline" className="h-10 self-end shrink-0" data-testid="proposta-carica" disabled={propBusy} onClick={loadPropRecips}><Users className="w-4 h-4 mr-1" /> Carica</Button>
                </div>
                {propLoaded && (
                  <div className="flex flex-wrap items-center justify-between text-xs text-slate-500 mb-1 gap-2">
                    <span data-testid="proposta-count" className="min-w-0">{propRecips.length} contatti · <b>{selCount}</b> selezionati</span>
                    <span className="flex gap-2 shrink-0">
                      <button className="underline" data-testid="proposta-sel-all" onClick={() => selVisibili(true)}>Seleziona con email</button>
                      <button className="underline" data-testid="proposta-sel-none" onClick={() => selVisibili(false)}>Deseleziona</button>
                    </span>
                  </div>
                )}
                <div className="max-h-72 overflow-auto border-2 border-slate-100 rounded-lg divide-y" data-testid="proposta-recipient-list">
                  {!propLoaded && <div className="p-4 text-center text-slate-400 text-sm">Premi "Carica" per elencare i contatti.</div>}
                  {propLoaded && propRecips.length === 0 && <div className="p-4 text-center text-slate-400 text-sm">Nessun contatto trovato in questa categoria.</div>}
                  {propRecips.map((f) => (
                    <label key={f.id} className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${!f.email ? "opacity-50" : ""}`} data-testid={`proposta-recip-${f.id}`}>
                      <input type="checkbox" className="w-4 h-4 accent-brand" disabled={!f.email} checked={!!propSel[f.id]} onChange={() => toggleSel(f.id)} />
                      <span className="min-w-0 flex-1">
                        <span className="font-semibold truncate block">{f.ragione_sociale}</span>
                        <span className="text-xs text-slate-500 truncate block">{f.email || "senza email"}{f.citta ? ` · ${f.citta}` : ""}</span>
                      </span>
                    </label>
                  ))}
                </div>
                <Button data-testid="proposta-invia" disabled={propBusy || selCount === 0} onClick={inviaProposta} className="w-full h-11 mt-3 bg-brand hover:bg-brand-dark font-bold">
                  <Send className="w-4 h-4 mr-2" /> {propBusy ? "Invio…" : `Invia proposta (${selCount})`}
                </Button>
                <p className="text-xs text-amber-600 mt-2">⚠️ In anteprima l'invio è limitato a 1 destinatario di test. In produzione raggiunge tutti i selezionati con email.</p>
              </div>
            </div>
          </Card>
        )}
      </section>

      {/* Iter122bp · Storico invii proposta */}
      <section data-testid="storico-proposte-section">
        <button
          type="button"
          onClick={() => setStoricoOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 p-3 rounded-lg border-2 border-slate-200 hover:bg-slate-50 transition-colors"
          data-testid="storico-proposte-toggle">
          <span className="inline-flex items-center gap-2">
            <History className="w-5 h-5 text-brand" />
            <h2 className="font-display text-lg font-bold text-slate-800">Storico invii proposta</h2>
            {storicoAggr && (
              <span className="text-xs text-slate-500 ml-2">
                {storicoAggr.totale_invii} invii · {storicoAggr.destinatari_unici} destinatari · {storicoAggr.campagne} campagne
              </span>
            )}
          </span>
          {storicoOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
        </button>

        {storicoOpen && (
          <div className="mt-3 space-y-2" data-testid="storico-proposte-list">
            {storico.length === 0 && <Card className="p-4 text-center text-slate-400 text-sm">Nessun invio registrato.</Card>}
            {storico.map((s) => (
              <div key={s.id} className="p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer"
                   data-testid={`storico-item-${s.id}`}
                   onClick={() => setStoricoDetail(s)}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm text-slate-800">{s.fornitore_nome || "(senza nome)"}</span>
                      {s.fornitore_citta && <span className="text-xs text-slate-500">· {s.fornitore_citta}</span>}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                      <Mail className="w-3 h-3" /> {s.email}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      <span className="italic">&quot;{s.oggetto?.slice(0, 80)}&quot;</span>
                    </div>
                  </div>
                  <div className="text-right text-xs text-slate-500 whitespace-nowrap">
                    <div className="font-semibold text-slate-700">{fmtDate(s.sent_at)}</div>
                    <div>{new Date(s.sent_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</div>
                    <div className="text-[10px] text-slate-400">da {s.sent_by_name || s.sent_by_email}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Iter122bp · Dettaglio invio */}
      {storicoDetail && (
        <Dialog open={!!storicoDetail} onOpenChange={(v) => !v && setStoricoDetail(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><Mail className="w-5 h-5 text-brand" /> Dettaglio invio</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-xs text-slate-500 font-semibold uppercase">Destinatario</span><div className="font-bold">{storicoDetail.fornitore_nome}</div><div className="text-xs text-slate-500">{storicoDetail.fornitore_citta}</div></div>
                <div><span className="text-xs text-slate-500 font-semibold uppercase">Email</span><div className="font-mono text-sm">{storicoDetail.email}</div></div>
                <div><span className="text-xs text-slate-500 font-semibold uppercase">Inviato il</span><div>{new Date(storicoDetail.sent_at).toLocaleString("it-IT")}</div></div>
                <div><span className="text-xs text-slate-500 font-semibold uppercase">Da</span><div>{storicoDetail.sent_by_name || storicoDetail.sent_by_email}</div></div>
              </div>
              <div>
                <span className="text-xs text-slate-500 font-semibold uppercase">Oggetto</span>
                <div className="mt-1 p-2 rounded bg-slate-50 border font-semibold">{storicoDetail.oggetto}</div>
              </div>
              <div>
                <span className="text-xs text-slate-500 font-semibold uppercase">Messaggio</span>
                <div className="mt-1 p-3 rounded bg-slate-50 border whitespace-pre-wrap text-xs">{storicoDetail.testo}</div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
